/**
 * Product Image Hooks
 * ===================
 * React Query hooks for product image operations.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { productImageApi } from '@/services/api'
import { productKeys } from '@/hooks/useProducts'
import type { ProductImageCreate, ProductImageUpdate } from '@/types'

/**
 * Hook to add an image by URL.
 */
export function useAddImageByUrl() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: string | number
      data: ProductImageCreate
    }) => productImageApi.addByUrl(productId, data),
    onSuccess: (_, { productId }) => {
      // Invalidate product detail to refresh images
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

/**
 * Hook to upload an image file.
 */
export function useUploadImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      productId,
      file,
      isPrimary = false,
    }: {
      productId: string | number
      file: File
      isPrimary?: boolean
    }) => productImageApi.upload(productId, file, isPrimary),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

/**
 * Hook to update an image (set primary, alt text).
 */
export function useUpdateImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      productId,
      imageId,
      data,
    }: {
      productId: string | number
      imageId: number
      data: ProductImageUpdate
    }) => productImageApi.update(productId, imageId, data),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

/**
 * Hook to delete an image.
 */
export function useDeleteImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      productId,
      imageId,
    }: {
      productId: string | number
      imageId: number
    }) => productImageApi.delete(productId, imageId),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

/**
 * Hook to set an image as primary.
 */
export function useSetPrimaryImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      productId,
      imageId,
    }: {
      productId: string | number
      imageId: number
    }) => productImageApi.setPrimary(productId, imageId),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}
